import { Accordion, AccordionHeader, AccordionStatus, AccordionTable } from '@dmc/ui';
import { useTranslation } from 'react-i18next';
import { GridControlled } from '@casops/components/tables/grid/GridControlled';
import { lockColumn } from '@casops/components/tables/grid/columnDefs';
import { ReactElement, useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { PersonnelSafetySettingsModel } from '@casops/store/apis/settingsApi';
import { ModelName } from '@casops/store/api';

const headerRowHeight = 38;
const rowHeight = 36;
const maxTableHeight = headerRowHeight + rowHeight * 4; // Show 4 rows before scrollbar is shown.

interface SafetyManagerTable {
  id: number;
  name: string;
}

interface SafetyManagersProps {
  safetyManagers: PersonnelSafetySettingsModel['safetyManagers'];
}

export const SafetyManagers = ({ safetyManagers }: SafetyManagersProps): ReactElement => {
  const { t } = useTranslation();

  const columnDefs = useMemo<ColDef<SafetyManagerTable>[]>(
    () => [{ field: 'name', headerName: t('Fields.Name') }],
    [t],
  );

  const rowData = useMemo<SafetyManagerTable[]>(() => {
    return safetyManagers.map((manager, idx) => ({
      id: idx,
      name: manager,
    }));
  }, [safetyManagers]);

  // This height will only be used if there are rows. The fallback display has its own styling.
  let tableHeight = maxTableHeight;
  if (rowData.length) {
    tableHeight = Math.min(rowData.length * rowHeight + headerRowHeight, maxTableHeight);
  }

  return (
    <Accordion
      defaultExpanded={false}
      status={AccordionStatus.Informative}
      icon="information"
      header={
        <AccordionHeader
          title={t('Field.SafetyManagers.Label')}
          subtitle={t('Field.SafetyManagers.Description')}
        />
      }
    >
      <AccordionTable
        hasRows={!!rowData.length}
        noRowsMessage={t('Model.Collection.NoItems', { modelName: ModelName.SafetyManager })}
      >
        <div style={{ width: '100%', height: tableHeight }}>
          <GridControlled<SafetyManagerTable>
            sliceKey="safety-managers"
            autoheight={false}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={lockColumn}
          />
        </div>
      </AccordionTable>
    </Accordion>
  );
};
